import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  createPurchaseItem,
  deletePurchaseItem,
  getPurchaseItemCategoryOptions,
  getPurchaseItems,
  patchPurchaseItem,
} from "../api/purchaseItemsApi";
import DeletePurchaseItemModal from "../components/DeletePurchaseItemModal";
import PurchaseItemFormModal from "../components/PurchaseItemFormModal";
import PurchaseItemsTable from "../components/PurchaseItemsTable";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 700;

const selectSx = {
  width: { xs: "100%", md: 180 },
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.500",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
  },
};

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

function errorToMessage(error, fallback) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    fallback
  );
}

function normalizeCategory(category) {
  if (!category?.id || !category?.name) return null;
  return { id: Number(category.id), name: String(category.name) };
}

function normalizePurchaseItem(item) {
  if (!item?.id) return null;
  const rawCategory =
    item.ingredient?.category || item.ingredient?.ingredientCategory;

  return {
    ...item,
    id: Number(item.id),
    brand: String(item.brand || ""),
    presentation: String(item.presentation || ""),
    purchaseUnit: String(item.purchaseUnit || ""),
    ingredient: item.ingredient
      ? {
          ...item.ingredient,
          id: Number(item.ingredient.id),
          name: String(item.ingredient.name || "Sin nombre"),
          category: normalizeCategory(rawCategory),
        }
      : null,
    supplier: item.supplier
      ? {
          ...item.supplier,
          id: Number(item.supplier.id),
          name: String(item.supplier.name || "Sin nombre"),
          phone: String(item.supplier.phone || "").trim() || null,
        }
      : null,
  };
}

function CardShell({ children }) {
  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
      }}
    >
      {children}
    </Box>
  );
}

export default function ItemsDeCompraPage() {
  const navigate = useNavigate();
  const latestRequestId = useRef(0);

  const [items, setItems] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [brandInput, setBrandInput] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [categoriesError, setCategoriesError] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchItems = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      setLoading(true);
      setListError("");

      try {
        const response = await getPurchaseItems({
          page,
          limit: PAGE_SIZE,
          brand: brandFilter || undefined,
          categoryId: categoryFilter || undefined,
          signal,
        });
        const payload = unwrapHttp(response);
        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        const normalized = list.map(normalizePurchaseItem).filter(Boolean);
        const responseTotalPages =
          Number(payload?.meta?.totalPages) || (normalized.length ? 1 : 0);

        if (responseTotalPages > 0 && page > responseTotalPages) {
          setPage(responseTotalPages);
          return;
        }

        setItems(normalized);
        setTotalItems(Number(payload?.meta?.totalItems) || normalized.length);
        setTotalPages(responseTotalPages);
        setExpandedIds((current) => {
          const visibleIds = new Set(normalized.map((item) => item.id));
          return new Set([...current].filter((id) => visibleIds.has(id)));
        });
      } catch (error) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        setListError(
          errorToMessage(error, "No se pudieron cargar los ítems de compra.")
        );
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [brandFilter, categoryFilter, page]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchItems(controller.signal);
    return () => controller.abort();
  }, [fetchItems, refreshVersion]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchCategoryOptions() {
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const response = await getPurchaseItemCategoryOptions({
          signal: controller.signal,
        });
        const payload = unwrapHttp(response);
        if (controller.signal.aborted) return;

        setCategoryOptions(
          (Array.isArray(payload) ? payload : [])
            .map(normalizeCategory)
            .filter(Boolean)
            .sort((left, right) =>
              left.name.localeCompare(right.name, "es", {
                sensitivity: "base",
              })
            )
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        setCategoryOptions([]);
        setCategoriesError(
          errorToMessage(error, "No se pudieron cargar las categorías.")
        );
      } finally {
        if (!controller.signal.aborted) setCategoriesLoading(false);
      }
    }

    fetchCategoryOptions();
    return () => controller.abort();
  }, []);

  const commitBrandFilter = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized === brandFilter) return;
      setBrandFilter(normalized);
      setPage(1);
      setExpandedIds(new Set());
    },
    [brandFilter]
  );

  useEffect(() => {
    const timeout = setTimeout(
      () => commitBrandFilter(brandInput),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timeout);
  }, [brandInput, commitBrandFilter]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin ítems de compra para mostrar";
    return `Mostrando ${items.length} de ${totalItems} ítems de compra`;
  }, [items.length, totalItems]);

  const visibleError = listError || categoriesError;

  function handleCategoryChange(value) {
    setCategoryFilter(value);
    setPage(1);
    setExpandedIds(new Set());
  }

  function handleClearBrand(event) {
    event.stopPropagation();
    setBrandInput("");
    setBrandFilter("");
    setPage(1);
    setExpandedIds(new Set());
  }

  function handleBrandKeyDown(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    commitBrandFilter(event.currentTarget.value);
  }

  function handleToggleExpanded(item) {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }

  function handleRequestDelete(item) {
    if (deletingId) return;
    setDeleteError("");
    setPendingDeleteItem(item);
  }

  function handleOpenCreate() {
    setEditingItem(null);
    setFormOpen(true);
  }

  function handleOpenEdit(item) {
    setEditingItem(item);
    setFormOpen(true);
  }

  function handleCloseForm() {
    setFormOpen(false);
    setEditingItem(null);
  }

  async function handleSaveItem(payload) {
    if (editingItem?.id) {
      await patchPurchaseItem(editingItem.id, payload);
    } else {
      await createPurchaseItem(payload);
    }

    const wasEditing = Boolean(editingItem?.id);
    handleCloseForm();
    setExpandedIds(new Set());
    setListError("");
    if (!wasEditing && page !== 1) setPage(1);
    else setRefreshVersion((current) => current + 1);
  }

  function handleCloseDelete() {
    if (deletingId) return;
    setDeleteError("");
    setPendingDeleteItem(null);
  }

  async function handleConfirmDelete() {
    const item = pendingDeleteItem;
    if (!item?.id || deletingId) return;

    setDeletingId(item.id);
    setDeleteError("");
    try {
      await deletePurchaseItem(item.id);
      const nextTotalItems = Math.max(0, totalItems - 1);
      const lastPage = Math.max(1, Math.ceil(nextTotalItems / PAGE_SIZE));
      setPendingDeleteItem(null);
      setExpandedIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });

      if (page > lastPage) setPage(lastPage);
      else setRefreshVersion((current) => current + 1);
    } catch (error) {
      const message = errorToMessage(
        error,
        "No se pudo eliminar el ítem de compra."
      );
      setDeleteError(message);
      setListError(message);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader
        title="Ítems de compra"
        onBack={() => navigate("/stock/base-de-datos")}
      />

      <CardShell>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Select
              value={categoryFilter}
              onChange={(event) => handleCategoryChange(event.target.value)}
              displayEmpty
              size="small"
              disabled={categoriesLoading}
              IconComponent={categoryFilter ? () => null : undefined}
              sx={{
                ...selectSx,
                "& .MuiSelect-select": {
                  ...selectSx["& .MuiSelect-select"],
                  pr: categoryFilter ? "32px !important" : undefined,
                },
              }}
              renderValue={(value) => {
                if (!value) {
                  return categoriesLoading
                    ? "Cargando categorías..."
                    : "Categoría";
                }
                return (
                  categoryOptions.find(
                    (category) => String(category.id) === String(value)
                  )?.name || "Categoría"
                );
              }}
              endAdornment={
                categoryFilter ? (
                  <InputAdornment
                    position="end"
                    sx={{ position: "absolute", right: 8, color: "primary.900" }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Limpiar filtro categoría"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCategoryChange("");
                      }}
                      sx={{ width: 24, height: 24, opacity: 0.7 }}
                    >
                      <ClearIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            >
              <MenuItem value="">Todas las categorías</MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category.id} value={String(category.id)}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "flex-end",
                flex: 1,
              }}
            >
              <TextField
                value={brandInput}
                onChange={(event) => setBrandInput(event.target.value)}
                onBlur={(event) => commitBrandFilter(event.target.value)}
                onKeyDown={handleBrandKeyDown}
                placeholder="Buscar por marca"
                size="small"
                slotProps={{ htmlInput: { maxLength: 150 } }}
                sx={{
                  width: { xs: "100%", md: 250 },
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "33px",
                    bgcolor: "neutral.50",
                    color: "primary.900",
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "neutral.700",
                    opacity: 0.9,
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: brandInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Limpiar búsqueda"
                        onClick={handleClearBrand}
                        sx={{ width: 24, height: 24, opacity: 0.7 }}
                      >
                        <ClearIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Button
                type="button"
                aria-label="Agregar ítem"
                onClick={handleOpenCreate}
                sx={{
                  minWidth: { xs: "100%", sm: 145 },
                  height: 38,
                  px: 2.4,
                  borderRadius: "33px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 800,
                  boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                  "&:hover": { bgcolor: "primary.600" },
                }}
              >
                Agregar ítem
              </Button>
            </Stack>
          </Stack>

          {!!visibleError && (
            <Box
              role="alert"
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Typography
                sx={{ color: "logoblanco", fontSize: 13, fontWeight: 800 }}
              >
                No se pudo completar la operación
              </Typography>
              <Typography
                sx={{
                  color: "logoblanco",
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: 0.95,
                }}
              >
                {visibleError}
              </Typography>
            </Box>
          )}

          <PurchaseItemsTable
            rows={items}
            loading={loading}
            deletingId={deletingId}
            expandedIds={expandedIds}
            onEdit={handleOpenEdit}
            onDelete={handleRequestDelete}
            onToggleExpanded={handleToggleExpanded}
            emptyMessage={
              brandFilter || categoryFilter
                ? "No se encontraron ítems con los filtros seleccionados."
                : "Todavía no hay ítems de compra cargados."
            }
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              pt: 0.5,
            }}
          >
            <Typography
              sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}
            >
              {pageSummary}
            </Typography>
            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => {
                setPage(value);
                setExpandedIds(new Set());
              }}
              shape="rounded"
              color="primary"
              disabled={loading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </CardShell>

      <PurchaseItemFormModal
        open={formOpen}
        item={editingItem}
        onClose={handleCloseForm}
        onSave={handleSaveItem}
      />

      <DeletePurchaseItemModal
        open={Boolean(pendingDeleteItem)}
        item={pendingDeleteItem}
        loading={Boolean(deletingId)}
        error={deleteError}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      />
    </Stack>
  );
}
